import React, { useEffect, useRef, useState } from 'react';
import { railBookingApi } from '../api/client';

// Booking a train from the rail price screen (task 4.21): the chosen train
// and class, the travellers, our list of train bookings, and per booking the
// travel documents and the tickets, refunding, voiding and exchanging.
//
// A refund shows what comes back before anything happens; the backend works it
// out from the ticket conditions without touching the booking. If less would
// come back when it is carried out, nothing is cancelled. An exchange shows
// what is due on top per train and class, and stops before payment if more
// is due than shown.
//
// We keep no traveller names, and Eurostar finds a booking only with its PNR
// and a traveller's last name. Right after booking the screen still knows the
// names; later it asks for one. Travel documents go straight to Eurostar.
//
// The backend searches again before it books, because the price can have
// moved since the table was shown. If it rose, nothing is paid and the screen
// asks to confirm the new price.

const CLASS_LABEL = { STANDARD: 'Standard', PLUS: 'Plus', PREMIER: 'Premier' };
const STATUS = {
  confirmed: ['Bevestigd', 'yes'],
  failed: ['Mislukt', 'no'],
  pending: ['Bezig', 'warning'],
  cancelled: ['Geannuleerd', 'no'],
};
const FAILURES = {
  price_changed: 'prijs gestegen, niet geboekt',
  refused: 'tegengehouden voor betaling',
  source_error: 'fout bij de bron',
};

// The station gates read only these characters; Eurostar refuses the rest.
const NAME_PATTERN = /^[- 'A-Za-zÀ-ÿ]+$/;

const money = (amount, currency) =>
  amount === null || amount === undefined
    ? ''
    : `${Number(amount).toLocaleString('nl-BE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} ${currency || ''}`.trim();

const clock = (iso) => (iso ? iso.slice(11, 16) : '');
const day = (iso) => (iso ? new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString('nl-BE') : '');
const moment = (iso) =>
  iso
    ? new Date(iso).toLocaleString('nl-BE', { dateStyle: 'short', timeStyle: 'short', timeZone: 'Europe/Brussels' })
    : '';

const errorDetail = (error) => (error.response && error.response.data && error.response.data.detail) || null;

// A panel opens above the list or below the price table, often outside the
// part of the page in view, and then a click seems to do nothing (Thomas,
// 28/10/2026). Every panel scrolls itself into view when it opens.
function useScrollIntoView() {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);
  return ref;
}

function StatusBadge({ status }) {
  const [label, kind] = STATUS[status] || [status, 'no'];
  return <span className={`badge ${kind}`}>{label}</span>;
}

// Eurostar's document types, the two we meet in practice first. A Belgian
// identity card is type I on its machine readable line.
const DOCUMENT_TYPES = [
  ['passport', 'Paspoort'],
  ['identity card - i', 'Identiteitskaart'],
  ['identity card - a', 'Identiteitskaart (type A)'],
  ['identity card - c', 'Identiteitskaart (type C)'],
  ['passport card', 'Paspoortkaart'],
  ['other', 'Ander document'],
];
const GENDERS = [
  ['F', 'vrouw'],
  ['M', 'man'],
  ['X', 'onbepaald'],
  ['U', 'niet opgegeven'],
];

function DocumentForm({ passenger, busy, onSubmit, onCancel }) {
  const [doc, setDoc] = useState({
    given_name: passenger.first_name,
    family_name: passenger.last_name,
    birth_date: '',
    gender: 'U',
    nationality: 'BEL',
    document_type: 'identity card - i',
    document_number: '',
    country_of_issue: 'BEL',
    expiry_date: '',
  });
  const set = (field, value) => setDoc({ ...doc, [field]: value });
  const submit = (e) => {
    e.preventDefault();
    onSubmit(doc);
  };
  return (
    <form onSubmit={submit} className="booking-form rail-document-form">
      <p className="intro">
        Reisdocument van {passenger.first_name} {passenger.last_name}, zoals op het document zelf. Alle voornamen bij
        voornamen. Landen met drie letters, zoals BEL of NLD. Gaat rechtstreeks naar Eurostar en het Britse Home Office,
        l2b bewaart het niet.
      </p>
      <div className="filter-bar">
        <label>
          Voornamen
          <input type="text" value={doc.given_name} onChange={(e) => set('given_name', e.target.value)} required />
        </label>
        <label>
          Achternaam
          <input type="text" value={doc.family_name} onChange={(e) => set('family_name', e.target.value)} required />
        </label>
        <label>
          Geboortedatum
          <input type="date" value={doc.birth_date} onChange={(e) => set('birth_date', e.target.value)} required />
        </label>
        <label>
          Geslacht
          <select value={doc.gender} onChange={(e) => set('gender', e.target.value)}>
            {GENDERS.map(([code, label]) => (
              <option key={code} value={code}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Nationaliteit
          <input type="text" maxLength="3" value={doc.nationality} onChange={(e) => set('nationality', e.target.value)} required />
        </label>
      </div>
      <div className="filter-bar">
        <label>
          Soort document
          <select value={doc.document_type} onChange={(e) => set('document_type', e.target.value)}>
            {DOCUMENT_TYPES.map(([code, label]) => (
              <option key={code} value={code}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Documentnummer
          <input type="text" value={doc.document_number} onChange={(e) => set('document_number', e.target.value)} required />
        </label>
        <label>
          Land van uitgifte
          <input
            type="text"
            maxLength="3"
            value={doc.country_of_issue}
            onChange={(e) => set('country_of_issue', e.target.value)}
            required
          />
        </label>
        <label>
          Vervaldatum
          <input type="date" value={doc.expiry_date} onChange={(e) => set('expiry_date', e.target.value)} required />
        </label>
      </div>
      <div className="button-row">
        <button type="submit" className="btn-primary" disabled={busy}>
          {busy ? 'Bezig' : 'Reisdocument doorgeven'}
        </button>{' '}
        <button type="button" className="btn-secondary" onClick={onCancel} disabled={busy}>
          Annuleren
        </button>
      </div>
    </form>
  );
}

export function RailBookingDocuments({ booking, knownLastName, onClose }) {
  const panel = useScrollIntoView();
  const [lastName, setLastName] = useState(knownLastName || '');
  const [passengers, setPassengers] = useState(null);
  const [emails, setEmails] = useState({});
  const [editing, setEditing] = useState(null);
  const [tickets, setTickets] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async (call) => {
    setBusy(true);
    setError(null);
    try {
      return await call();
    } catch (e) {
      const detail = errorDetail(e);
      setError(typeof detail === 'string' ? detail : 'Dat is niet gelukt.');
      return null;
    } finally {
      setBusy(false);
    }
  };

  const load = async (e) => {
    if (e) e.preventDefault();
    const r = await run(() => railBookingApi.passengers(booking.id, lastName.trim()));
    if (r) {
      setPassengers(r.data);
      setTickets(null);
    }
  };

  // Right after booking the last name is known, so look the travellers up.
  React.useEffect(() => {
    if (knownLastName) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveEmails = async () => {
    const filled = Object.fromEntries(Object.entries(emails).filter(([, v]) => v && v.trim()));
    const r = await run(() => railBookingApi.contacts(booking.id, lastName.trim(), filled));
    if (r) {
      setPassengers(r.data);
      setEmails({});
    }
  };

  const saveDocument = async (passengerId, doc) => {
    const r = await run(() =>
      railBookingApi.travelDocument(booking.id, lastName.trim(), { passenger_id: passengerId, ...doc })
    );
    if (r) {
      setPassengers(r.data);
      setEditing(null);
    }
  };

  const fetchTickets = async () => {
    const r = await run(() => railBookingApi.tickets(booking.id, lastName.trim(), 'nl'));
    if (r) setTickets(r.data);
  };

  const ready = passengers && passengers.every((p) => p.has_contact && !p.missing_documents);
  const withoutContact = passengers ? passengers.filter((p) => !p.has_contact) : [];

  return (
    <div ref={panel} className="booking-form rail-documents">
      <h3>
        Reisdocumenten en tickets: PNR {booking.reference}
      </h3>
      <p className="intro">
        {booking.origin} → {booking.destination} · {day(booking.travel_date)} · trein {booking.service_name}
        {booking.departure_at ? `, vertrek ${clock(booking.departure_at)}` : ''} · {booking.persons}{' '}
        {booking.persons === 1 ? 'reiziger' : 'reizigers'}
      </p>

      <form onSubmit={load} className="filter-bar">
        <label>
          Achternaam van een reiziger
          <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} required />
        </label>
        <div className="button-row">
          <button type="submit" className="btn-secondary" disabled={busy || !lastName.trim()}>
            {passengers ? 'Opnieuw ophalen' : 'Reizigers ophalen'}
          </button>
        </div>
      </form>
      <p className="small-line">
        Eurostar vindt een boeking alleen met de PNR en de achternaam van een reiziger. l2b bewaart geen namen.
      </p>

      {error && <div className="message error">{error}</div>}

      {passengers && (
        <table className="insights-table">
          <thead>
            <tr>
              <th>Reiziger</th>
              <th>Contact</th>
              <th>Reisdocument</th>
            </tr>
          </thead>
          <tbody>
            {passengers.map((p) => (
              <tr key={p.passenger_id}>
                <td>
                  {p.first_name} {p.last_name}
                </td>
                <td>
                  {p.has_contact ? (
                    <span className="badge yes">in orde</span>
                  ) : (
                    <input
                      type="email"
                      placeholder="e-mailadres"
                      value={emails[p.passenger_id] || ''}
                      onChange={(e) => setEmails({ ...emails, [p.passenger_id]: e.target.value })}
                    />
                  )}
                </td>
                <td>
                  {!p.documents_required && <span className="badge no">niet nodig</span>}
                  {p.documents_required && p.ready_to_travel && <span className="badge yes">in orde</span>}
                  {p.missing_documents && (
                    <>
                      <span className="badge warning">ontbreekt</span>{' '}
                      <button type="button" className="link-button" onClick={() => setEditing(p)} disabled={busy}>
                        Invullen
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {withoutContact.length > 0 && (
        <div className="button-row">
          <p className="small-line">Zonder e-mailadres of telefoonnummer per reiziger geeft Eurostar geen tickets.</p>
          <button
            type="button"
            className="btn-secondary"
            onClick={saveEmails}
            disabled={busy || !withoutContact.some((p) => (emails[p.passenger_id] || '').trim())}
          >
            E-mailadressen doorgeven
          </button>
        </div>
      )}

      {editing && (
        <DocumentForm
          key={editing.passenger_id}
          passenger={editing}
          busy={busy}
          onSubmit={(doc) => saveDocument(editing.passenger_id, doc)}
          onCancel={() => setEditing(null)}
        />
      )}

      {passengers && (
        <div className="button-row">
          <button type="button" className="btn-primary" onClick={fetchTickets} disabled={busy || !ready}>
            {busy ? 'Bezig' : 'Tickets ophalen'}
          </button>{' '}
          <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
            Sluiten
          </button>
          {!ready && <span className="small-line"> Eerst een contact en, waar nodig, een reisdocument per reiziger.</span>}
        </div>
      )}

      {tickets && (
        <table className="insights-table">
          <thead>
            <tr>
              <th>Reiziger</th>
              <th>Trein</th>
              <th>Vertrek</th>
              <th>Rijtuig en plaats</th>
              <th>Ticket</th>
            </tr>
          </thead>
          <tbody>
            {tickets.map((t) => (
              <tr key={`${t.passenger_id}-${t.direction}-${t.train}`}>
                <td>
                  {t.first_name} {t.last_name}
                </td>
                <td>
                  {t.train}
                  <div className="small-line">
                    {t.origin} → {t.destination}
                  </div>
                </td>
                <td>
                  {day(t.departure_date)} {t.departure_time}
                </td>
                <td>{t.coach || t.seat ? `rijtuig ${t.coach || '?'}, plaats ${t.seat || '?'}` : '-'}</td>
                <td>
                  {t.pdf_url ? (
                    <a href={t.pdf_url} target="_blank" rel="noopener noreferrer">
                      Pdf downloaden
                    </a>
                  ) : (
                    '-'
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function RailBookingForm({ result, train, fare, onDone, onCancel }) {
  const panel = useScrollIntoView();
  const persons = fare.persons || result.adults;
  const [travellers, setTravellers] = useState(() =>
    Array.from({ length: persons }, () => ({ first_name: '', last_name: '', email: '' }))
  );
  const [acceptTotal, setAcceptTotal] = useState(fare.price);
  const [priceRise, setPriceRise] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const update = (i, field, value) =>
    setTravellers(travellers.map((t, j) => (j === i ? { ...t, [field]: value } : t)));

  const invalidName = travellers.some(
    (t) => (t.first_name && !NAME_PATTERN.test(t.first_name.trim())) || (t.last_name && !NAME_PATTERN.test(t.last_name.trim()))
  );

  const book = async (total) => {
    setBusy(true);
    setError(null);
    try {
      const r = await railBookingApi.create({
        transport_leg_id: result.transport_leg_id,
        travel_date: result.travel_date,
        service_name: train.service_name,
        fare_class: fare.fare_class,
        accept_total: total,
        travellers: travellers.map((t) => ({
          first_name: t.first_name.trim(),
          last_name: t.last_name.trim(),
          email: t.email.trim() || null,
        })),
      });
      onDone(r.data, travellers[0].last_name.trim());
    } catch (e) {
      const detail = errorDetail(e);
      if (e.response && e.response.status === 409 && detail && detail.new_total) {
        setPriceRise(detail);
      } else {
        setError(typeof detail === 'string' ? detail : 'Het boeken is niet gelukt.');
      }
    } finally {
      setBusy(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    if (invalidName) return;
    book(acceptTotal);
  };

  const acceptRise = () => {
    setAcceptTotal(priceRise.new_total);
    setPriceRise(null);
    book(priceRise.new_total);
  };

  return (
    <form onSubmit={submit} ref={panel} className="booking-form rail-booking-form">
      <h3>
        Boeken: trein {train.service_name}, {CLASS_LABEL[fare.fare_class] || fare.fare_class}
      </h3>
      <p className="intro">
        {result.company} · {result.transport_leg_slug} · {day(result.travel_date)} · vertrek {clock(train.departure_at)} ·{' '}
        {money(acceptTotal, fare.currency)} voor {persons} {persons === 1 ? 'reiziger' : 'reizigers'}. De namen zoals op
        de identiteitskaart, zonder cijfers of andere tekens: de poortjes in het station lezen alleen letters, accenten,
        spaties, koppeltekens en apostrofs. Een e-mailadres per reiziger is nodig om later tickets te krijgen, maar niet om
        te boeken.
      </p>
      {result.environment === 'sandbox' && (
        <div className="message warning">Testomgeving: dit wordt een testboeking, er wordt niets echt gekocht.</div>
      )}
      {travellers.map((t, i) => (
        <div className="filter-bar" key={i}>
          <span className="room-number">Reiziger {i + 1}</span>
          <label>
            Voornaam
            <input type="text" value={t.first_name} onChange={(e) => update(i, 'first_name', e.target.value)} required />
          </label>
          <label>
            Achternaam
            <input type="text" value={t.last_name} onChange={(e) => update(i, 'last_name', e.target.value)} required />
          </label>
          <label>
            E-mail (niet verplicht)
            <input type="email" value={t.email} onChange={(e) => update(i, 'email', e.target.value)} />
          </label>
        </div>
      ))}
      {invalidName && (
        <div className="message error">Een naam bevat een teken dat Eurostar niet aanvaardt, zoals een cijfer.</div>
      )}
      {priceRise && (
        <div className="message warning">
          {priceRise.message}{' '}
          <button type="button" className="btn-primary" onClick={acceptRise} disabled={busy}>
            Boeken voor {money(priceRise.new_total, fare.currency)}
          </button>
        </div>
      )}
      {error && <div className="message error">{error}</div>}
      <div className="button-row">
        <button type="submit" className="btn-primary" disabled={busy || invalidName || !!priceRise}>
          {busy ? 'Bezig met boeken' : `Boeken voor ${money(acceptTotal, fare.currency)}`}
        </button>{' '}
        <button type="button" className="btn-secondary" onClick={onCancel} disabled={busy}>
          Annuleren
        </button>
      </div>
    </form>
  );
}

export function RailBookingList({ bookings, onDocuments, onRefund, onExchange }) {
  if (!bookings.length) return <p className="intro">Nog geen treinboekingen.</p>;
  return (
    <table className="insights-table">
      <thead>
        <tr>
          <th>Gemaakt</th>
          <th>Reisonderdeel</th>
          <th>Reisdatum</th>
          <th>Trein</th>
          <th>Klasse</th>
          <th className="number">Reizigers</th>
          <th className="number">Prijs</th>
          <th>PNR</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        {bookings.map((b) => (
          <tr key={b.id}>
            <td>{moment(b.created_at)}</td>
            <td>
              {b.origin || '?'} → {b.destination || '?'}
              <div className="small-line">{b.company}</div>
            </td>
            <td>{day(b.travel_date)}</td>
            <td>
              {b.service_name}
              {b.departure_at && <div className="small-line">vertrek {clock(b.departure_at)}</div>}
            </td>
            <td>{CLASS_LABEL[b.fare_class] || b.fare_class}</td>
            <td className="number">{b.persons}</td>
            <td className="number">{money(b.total !== null && b.total !== undefined ? b.total : b.accepted_total, b.currency)}</td>
            <td>
              <strong>{b.reference || '-'}</strong>
              {b.environment === 'sandbox' && (
                <div className="small-line">
                  <span className="badge">test</span>
                </div>
              )}
            </td>
            <td>
              <StatusBadge status={b.status} />
              {b.status === 'failed' && (
                <div className="small-line">{FAILURES[b.error_code] || b.error_code || 'reden onbekend'}</div>
              )}
              {b.status === 'cancelled' && (
                <div className="small-line">
                  {money(b.refunded, b.currency)} terug
                  {Number(b.cancellation_fee) > 0 ? `, ${money(b.cancellation_fee, b.currency)} kosten` : ''}
                </div>
              )}
              {b.exchanged_at && b.status === 'confirmed' && (
                <div className="small-line">omgeboekt op {moment(b.exchanged_at)}</div>
              )}
              {b.status === 'confirmed' && (
                <div className="booking-actions">
                  {onDocuments && (
                    <button type="button" className="link-button" onClick={() => onDocuments(b)}>
                      Reisdocumenten en tickets
                    </button>
                  )}
                  {onExchange && (
                    <button type="button" className="link-button" onClick={() => onExchange(b)}>
                      Omboeken
                    </button>
                  )}
                  {onRefund && (
                    <button type="button" className="link-button" onClick={() => onRefund(b)}>
                      {b.can_void ? 'Terugbetalen of voiden' : 'Terugbetalen'}
                    </button>
                  )}
                </div>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function LastNameField({ value, onChange }) {
  return (
    <label>
      Achternaam van een reiziger
      <input type="text" value={value} onChange={(e) => onChange(e.target.value)} required />
    </label>
  );
}

export function RailBookingRefund({ booking, knownLastName, onDone, onClose }) {
  const panel = useScrollIntoView();
  const [lastName, setLastName] = useState(knownLastName || '');
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async (call) => {
    setBusy(true);
    setError(null);
    try {
      return await call();
    } catch (e) {
      const detail = errorDetail(e);
      setError(typeof detail === 'string' ? detail : 'Dat is niet gelukt.');
      return null;
    } finally {
      setBusy(false);
    }
  };

  const ask = async (e) => {
    e.preventDefault();
    const r = await run(() => railBookingApi.refundQuote(booking.id, lastName.trim()));
    if (r) setQuote(r.data);
  };

  const refund = async () => {
    if (!window.confirm(`PNR ${booking.reference} annuleren? Er komt ${money(quote.refund, quote.currency)} terug. Dit kan niet ongedaan gemaakt worden.`)) return;
    const r = await run(() => railBookingApi.refund(booking.id, lastName.trim(), quote.refund));
    if (r) onDone(r.data, `Terugbetaald: PNR ${booking.reference}, ${money(r.data.refunded, r.data.currency)} terug.`);
    else setQuote(null);
  };

  const voidBooking = async () => {
    if (!window.confirm(`PNR ${booking.reference} voiden? Alles komt terug. Dit kan niet ongedaan gemaakt worden.`)) return;
    const r = await run(() => railBookingApi.void(booking.id, lastName.trim()));
    if (r) onDone(r.data, `Gevoid: PNR ${booking.reference}, ${money(r.data.refunded, r.data.currency)} terug.`);
  };

  return (
    <div ref={panel} className="booking-form rail-refund">
      <h3>Terugbetalen: PNR {booking.reference}</h3>
      <p className="intro">
        {booking.origin} → {booking.destination} · {day(booking.travel_date)} · trein {booking.service_name} ·{' '}
        {money(booking.total, booking.currency)} voor {booking.persons}. De hele boeking wordt geannuleerd. Wat er
        terugkomt, volgt uit de voorwaarden van de tickets en wordt eerst getoond; er gebeurt niets voor je bevestigt.
      </p>
      <form onSubmit={ask} className="filter-bar">
        <LastNameField value={lastName} onChange={setLastName} />
        <div className="button-row">
          <button type="submit" className="btn-secondary" disabled={busy || !lastName.trim()}>
            {quote ? 'Opnieuw opvragen' : 'Bedrag opvragen'}
          </button>
        </div>
      </form>
      {error && <div className="message error">{error}</div>}
      {quote && (
        <div>
          <p>
            Terug: <strong>{money(quote.refund, quote.currency)}</strong>
            {Number(quote.fee) > 0 ? `, kosten ${money(quote.fee, quote.currency)}` : ', zonder kosten'} ·{' '}
            {quote.tickets} {quote.tickets === 1 ? 'ticket' : 'tickets'}
          </p>
          <div className="button-row">
            <button type="button" className="btn-primary" onClick={refund} disabled={busy}>
              {busy ? 'Bezig' : `Terugbetalen voor ${money(quote.refund, quote.currency)}`}
            </button>{' '}
            {quote.can_void && (
              <button type="button" className="btn-secondary" onClick={voidBooking} disabled={busy}>
                Voiden: alles terug
              </button>
            )}{' '}
            <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
              Sluiten
            </button>
          </div>
          {quote.can_void && (
            <p className="small-line">Voiden kan tot 24 uur na de bevestiging, zolang er niet omgeboekt is.</p>
          )}
        </div>
      )}
      {!quote && (
        <div className="button-row">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
            Sluiten
          </button>
        </div>
      )}
    </div>
  );
}

export function RailBookingExchange({ booking, knownLastName, onDone, onClose }) {
  const panel = useScrollIntoView();
  const [lastName, setLastName] = useState(knownLastName || '');
  const [travelDate, setTravelDate] = useState(booking.travel_date);
  const [offers, setOffers] = useState(null);
  const [rise, setRise] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async (call) => {
    setBusy(true);
    setError(null);
    try {
      return await call();
    } catch (e) {
      const detail = errorDetail(e);
      if (e.response && e.response.status === 409 && detail && detail.new_total) {
        return { rise: detail };
      }
      setError(typeof detail === 'string' ? detail : 'Dat is niet gelukt.');
      return null;
    } finally {
      setBusy(false);
    }
  };

  const ask = async (e) => {
    e.preventDefault();
    setRise(null);
    const r = await run(() => railBookingApi.exchangeOffers(booking.id, lastName.trim(), travelDate));
    if (r && r.data) setOffers(r.data.map((o) => ({ ...o, travel_date: travelDate })));
  };

  const pick = async (offer, accept) => {
    const amount = accept !== undefined ? accept : offer.difference;
    if (
      accept === undefined &&
      !window.confirm(
        `PNR ${booking.reference} omboeken naar trein ${offer.service_name} op ${day(offer.travel_date)}, ` +
          `${CLASS_LABEL[offer.fare_class] || offer.fare_class}? Bij te betalen: ${money(amount, offer.currency)}.`
      )
    )
      return;
    const r = await run(() => railBookingApi.exchange(booking.id, lastName.trim(), offer, amount));
    if (r && r.rise) setRise({ offer, ...r.rise });
    else if (r) onDone(r.data, `Omgeboekt: PNR ${booking.reference} rijdt nu met trein ${r.data.service_name} op ${day(r.data.travel_date)}.`);
  };

  const classes = offers ? ['STANDARD', 'PLUS', 'PREMIER'].filter((c) => offers.some((o) => o.fare_class === c)) : [];
  const trains = offers
    ? [...new Map(offers.map((o) => [`${o.service_name}-${o.departure_at}`, o])).values()].sort((a, b) =>
        (a.departure_at || '').localeCompare(b.departure_at || '')
      )
    : [];

  return (
    <div ref={panel} className="booking-form rail-exchange">
      <h3>Omboeken: PNR {booking.reference}</h3>
      <p className="intro">
        Nu: {booking.origin} → {booking.destination} · {day(booking.travel_date)} · trein {booking.service_name},{' '}
        {CLASS_LABEL[booking.fare_class] || booking.fare_class} · {booking.persons}{' '}
        {booking.persons === 1 ? 'reiziger' : 'reizigers'}. Kies een dag en een trein; het bedrag is wat er bij moet
        voor het hele gezelschap. Is een ticket goedkoper, dan komt het verschil niet terug.
      </p>
      <form onSubmit={ask} className="filter-bar">
        <LastNameField value={lastName} onChange={setLastName} />
        <label>
          Nieuwe reisdatum
          <input type="date" value={travelDate} onChange={(e) => setTravelDate(e.target.value)} required />
        </label>
        <div className="button-row">
          <button type="submit" className="btn-secondary" disabled={busy || !lastName.trim()}>
            Treinen opvragen
          </button>{' '}
          <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
            Sluiten
          </button>
        </div>
      </form>
      {error && <div className="message error">{error}</div>}
      {rise && (
        <div className="message warning">
          {rise.message}{' '}
          <button type="button" className="btn-primary" onClick={() => pick(rise.offer, rise.new_total)} disabled={busy}>
            Omboeken voor {money(rise.new_total, rise.offer.currency)} erbij
          </button>
        </div>
      )}
      {offers && offers.length === 0 && <p className="intro">Geen treinen in ruil op die dag.</p>}
      {trains.length > 0 && (
        <table className="insights-table">
          <thead>
            <tr>
              <th>Vertrek</th>
              <th>Aankomst</th>
              <th>Trein</th>
              {classes.map((c) => (
                <th key={c} className="number">
                  {CLASS_LABEL[c] || c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {trains.map((t) => (
              <tr key={`${t.service_name}-${t.departure_at}`}>
                <td>{clock(t.departure_at)}</td>
                <td>{clock(t.arrival_at)}</td>
                <td>{t.service_name}</td>
                {classes.map((c) => {
                  const o = offers.find((x) => x.service_name === t.service_name && x.departure_at === t.departure_at && x.fare_class === c);
                  if (!o) return <td key={c} className="number empty">-</td>;
                  if (!o.bookable) {
                    return (
                      <td key={c} className="number">
                        <span className="badge no">niet beschikbaar</span>
                      </td>
                    );
                  }
                  const current = t.service_name === booking.service_name && o.travel_date === booking.travel_date && c === booking.fare_class;
                  return (
                    <td key={c} className="number">
                      <strong>{Number(o.difference) > 0 ? `+ ${money(o.difference, o.currency)}` : 'zonder bijbetaling'}</strong>
                      <div className="small-line">{o.seats !== null && o.seats !== undefined ? `${o.seats} aan deze prijs` : ''}</div>
                      {current ? (
                        <div className="small-line">huidige trein</div>
                      ) : (
                        <button type="button" className="link-button" onClick={() => pick(o)} disabled={busy}>
                          Omboeken
                        </button>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
